/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  FormControlLabel, 
  Switch, 
  Button, 
  Divider, 
  Alert, 
  CircularProgress,
  Card,
  CardContent,
  Grid,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { 
  Email as EmailIcon,
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';

// Consistent API path helper (same pattern as debateService.ts)
const API_URL: string = '';

const makeApiPath = (path: string) => {
  if (API_URL === '') {
    return `/api${path.startsWith('/') ? path : '/' + path}`;
  }
  
  if (API_URL.endsWith('/api')) {
    return `${API_URL}${path.startsWith('/') ? path : '/' + path}`;
  }
  
  return `${API_URL}/api${path.startsWith('/') ? path : '/' + path}`;
};

// Client-side interface for user settings
interface UserSettings {
  emailNotifications: {
    onTurn: boolean;
    onDebateEndingSoon: boolean;
    reformulationNotifications: boolean;
  }
}

// Server-side interface for sending/receiving data
interface ServerUserSettings {
  emailNotifications: {
    turnReminders: boolean;
    deadlineAlerts: boolean;
    reformulationNotifications: boolean;
  }
}

const UserSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<UserSettings>({
    emailNotifications: {
      onTurn: false,
      onDebateEndingSoon: false,
      reformulationNotifications: false
    }
  });
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordValidationErrors, setPasswordValidationErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    general: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Transform client settings to server format
  const clientToServerSettings = (clientSettings: UserSettings): ServerUserSettings => {
    return {
      emailNotifications: {
        turnReminders: clientSettings.emailNotifications.onTurn,
        deadlineAlerts: clientSettings.emailNotifications.onDebateEndingSoon,
        reformulationNotifications: clientSettings.emailNotifications.reformulationNotifications
      }
    };
  };

  // Transform server settings to client format
  const serverToClientSettings = (serverSettings: any): UserSettings => {
    return {
      emailNotifications: {
        onTurn: serverSettings.emailNotifications?.turnReminders ?? false,
        onDebateEndingSoon: serverSettings.emailNotifications?.deadlineAlerts ?? false,
        reformulationNotifications: serverSettings.emailNotifications?.reformulationNotifications ?? false
      }
    };
  };

  useEffect(() => {
    const fetchSettings = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('settingsPage.loginRequired'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // 🔧 FIX: Use consistent API pattern like other services
        const response = await axios.get(makeApiPath('/users/settings'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          // Transform server settings to client format
          setSettings(serverToClientSettings(response.data));
        }
      } catch (err) {
        logger.error('Failed to fetch user settings:', err);
        setError(t('settingsPage.failedToLoadSettings'));
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [t]);

  const handleSaveSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('settingsPage.loginRequired'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      // Transform client settings to server format
      const serverSettings = clientToServerSettings(settings);
      // 🔧 FIX: Use consistent API pattern like other services
      await axios.put(makeApiPath('/users/settings'), serverSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      toast.success(t('settingsPage.settingsSavedSuccessfully'));
    } catch (err) {
      logger.error('Failed to save user settings:', err);
      setError(t('settingsPage.failedToSaveSettings'));
      toast.error(t('settingsPage.failedToSaveSettings'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (setting: 'onTurn' | 'onDebateEndingSoon' | 'reformulationNotifications') => {
    setSettings(prev => ({
      ...prev,
      emailNotifications: {
        ...prev.emailNotifications,
        [setting]: !prev.emailNotifications[setting]
      }
    }));
  };

  const handlePasswordChange = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPasswordValidationErrors(prev => ({ ...prev, general: t('settingsPage.loginRequired') }));
      return;
    }

    // Clear previous validation errors
    setPasswordValidationErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      general: ''
    });

    let hasErrors = false;

    // Validate current password
    if (!passwordData.currentPassword.trim()) {
      setPasswordValidationErrors(prev => ({ ...prev, currentPassword: t('settingsPage.currentPasswordRequired') }));
      hasErrors = true;
    }

    // Validate new password
    if (!passwordData.newPassword.trim()) {
      setPasswordValidationErrors(prev => ({ ...prev, newPassword: t('settingsPage.newPasswordRequired') }));
      hasErrors = true;
    } else if (passwordData.newPassword.length < 8) {
      setPasswordValidationErrors(prev => ({ ...prev, newPassword: t('settingsPage.passwordMinLength') }));
      hasErrors = true;
    }

    // Validate confirm password
    if (!passwordData.confirmPassword.trim()) {
      setPasswordValidationErrors(prev => ({ ...prev, confirmPassword: t('settingsPage.confirmPasswordRequired') }));
      hasErrors = true;
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordValidationErrors(prev => ({ ...prev, confirmPassword: t('settingsPage.passwordsDoNotMatch') }));
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    try {
      setChangingPassword(true);
      
      await axios.put(makeApiPath('/auth/change-password'), {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset form on success
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      toast.success(t('settingsPage.passwordChangedSuccessfully'));
    } catch (err: any) {
      logger.error('Failed to change password:', err);
      if (err.response?.status === 400) {
        setPasswordValidationErrors(prev => ({ ...prev, currentPassword: t('settingsPage.currentPasswordIncorrect') }));
      } else {
        setPasswordValidationErrors(prev => ({ ...prev, general: t('settingsPage.failedToChangePassword') }));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePasswordFieldChange = (field: 'currentPassword' | 'newPassword' | 'confirmPassword', value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (passwordValidationErrors[field]) {
      setPasswordValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>{t('settingsPage.loadingSettings')}</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {t('settingsPage.title')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('settingsPage.settingsSavedSuccessfully')}
          </Alert>
        )}
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <NotificationsIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t('settingsPage.emailNotifications')}</Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('settingsPage.emailNotificationsDescription')}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ py: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.emailNotifications.onTurn}
                        onChange={() => handleToggle('onTurn')}
                        color="primary"
                      />
                    }
                    label={t('settingsPage.notifyOnTurn')}
                  />
                </Box>
                
                <Box sx={{ py: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.emailNotifications.onDebateEndingSoon}
                        onChange={() => handleToggle('onDebateEndingSoon')}
                        color="primary"
                      />
                    }
                    label={t('settingsPage.notifyOnDebateEndingSoon')}
                  />
                </Box>
                
                <Box sx={{ py: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.emailNotifications.reformulationNotifications}
                        onChange={() => handleToggle('reformulationNotifications')}
                        color="primary"
                      />
                    }
                    label={t('settingsPage.notifyOnReformulation')}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t('settingsPage.emailAddress')}</Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('settingsPage.currentEmailAddress')} <strong>{user?.email}</strong>
                </Typography>
                
                {/* Future enhancement: Allow email address change */}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LockIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t('settingsPage.changePassword')}</Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('settingsPage.changePasswordDescription')}
                </Typography>
                
                {passwordValidationErrors.general && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {passwordValidationErrors.general}
                  </Alert>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('settingsPage.currentPassword')}
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordFieldChange('currentPassword', e.target.value)}
                    error={!!passwordValidationErrors.currentPassword}
                    helperText={passwordValidationErrors.currentPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('current')}
                            edge="end"
                          >
                            {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label={t('settingsPage.newPassword')}
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordFieldChange('newPassword', e.target.value)}
                    error={!!passwordValidationErrors.newPassword}
                    helperText={passwordValidationErrors.newPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('new')}
                            edge="end"
                          >
                            {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label={t('settingsPage.confirmNewPassword')}
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordFieldChange('confirmPassword', e.target.value)}
                    error={!!passwordValidationErrors.confirmPassword}
                    helperText={passwordValidationErrors.confirmPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('confirm')}
                            edge="end"
                          >
                            {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={changingPassword ? <CircularProgress size={20} /> : <LockIcon />}
                      onClick={handlePasswordChange}
                      disabled={changingPassword}
                    >
                      {changingPassword ? t('settingsPage.changingPassword') : t('settingsPage.changePassword')}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSaveSettings}
                disabled={saving}
              >
                {saving ? t('settingsPage.saving') : t('settingsPage.saveSettings')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default UserSettingsPage; 